export type Vibe = 'Relaxed and quiet' | 'Relaxed but social' | 'Lively and fun' | 'Party atmosphere';
export type CrowdSize = 'Small, private feel' | 'Small group' | 'Medium group' | 'Large / crowded';
export type Boarding = 'From dock' | 'Transfer boat' | 'Walk / swim in water';
export type BestFor = 'Solo travelers' | 'Couples' | 'Families' | 'Friend groups';
export type AvailableTo = 'Hotel guests' | 'Cruise passengers';
export type BoatType = 'Sailboat' | 'Catamaran' | 'Power Catamaran' | 'Speedboat' | 'Donut';
export type Category = 'Sunset' | 'Sail & Snorkel' | 'Dinner' | 'Diving' | 'Around the island' | 'Rental & Charter';
export type TimeOfDay = 'Morning' | 'Afternoon' | 'Sunset';
export type DeparturePoint = 'Palm Beach' | 'Eagle Beach' | 'Oranjestad' | 'Baby Beach';
export type PickUp = 'Optional' | 'Included' | 'Not available';

export type PlatformName = 'Viator' | 'GetYourGuide' | 'Tripadvisor' | 'Expedia' | 'Direct booking';

// Rating fields store "1"-"5" as text, or special values like "No alcohol" for open_bar
export type RatingValue = '1' | '2' | '3' | '4' | '5';
export type OpenBarValue = RatingValue | 'No alcohol';

export interface TourPlatform {
  id: string;
  tour_id: string;
  platform_name: PlatformName;
  link: string;
  price: number;
  currency: string;
  review_rating: number | null;
  review_count: number | null;
  click_count?: number;
}

export interface Tour {
  id: string;
  title: string;
  company_name: string | null;
  short_description: string | null;
  inclusions: string[] | null;
  itinerary: string[] | null;
  pros: string[] | null;
  cons: string[] | null;
  reviews_summary: string | null;
  cover_photo_url: string | null;
  vibe: Vibe | null;
  crowd_size: CrowdSize | null;
  snorkeling_quality: string | null; // "1"-"5"
  food_quality: string | null; // "1"-"5"
  open_bar: string | null; // "1"-"5" or "No alcohol"
  available_to: AvailableTo[] | null;
  crew: string | null; // "1"-"5"
  boarding: Boarding | null;
  best_for: BestFor[] | null;
  boat_type: BoatType | null;
  categories: Category[] | null;
  time_of_day: TimeOfDay[] | null;
  duration_hours: number | null;
  departure_point: DeparturePoint | null;
  pick_up: PickUp | null;
  activity_type: string;
  island: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  tour_platforms?: TourPlatform[];
  // Legacy fields kept for DB compat but not used in UI
  music?: string | null;
  family_suitability?: string | null;
  shore_access?: string | null;
  seasickness_risk?: string | null;
}

export const VIBE_OPTIONS: Vibe[] = ['Relaxed and quiet', 'Relaxed but social', 'Lively and fun', 'Party atmosphere'];
export const CROWD_SIZE_OPTIONS: CrowdSize[] = ['Small, private feel', 'Small group', 'Medium group', 'Large / crowded'];
export const AVAILABLE_TO_OPTIONS: AvailableTo[] = ['Hotel guests', 'Cruise passengers'];
export const BOARDING_OPTIONS: Boarding[] = ['From dock', 'Transfer boat', 'Walk / swim in water'];
export const BEST_FOR_OPTIONS: BestFor[] = ['Solo travelers', 'Couples', 'Families', 'Friend groups'];
export const BOAT_TYPE_OPTIONS: BoatType[] = ['Sailboat', 'Catamaran', 'Power Catamaran', 'Speedboat', 'Donut'];
export const CATEGORY_OPTIONS: Category[] = ['Sunset', 'Sail & Snorkel', 'Dinner', 'Diving', 'Around the island', 'Rental & Charter'];
export const TIME_OF_DAY_OPTIONS: TimeOfDay[] = ['Morning', 'Afternoon', 'Sunset'];
export const DEPARTURE_POINT_OPTIONS: DeparturePoint[] = ['Palm Beach', 'Eagle Beach', 'Oranjestad', 'Baby Beach'];
export const PICK_UP_OPTIONS: PickUp[] = ['Optional', 'Included', 'Not available'];
export const PLATFORM_OPTIONS: PlatformName[] = ['Viator', 'GetYourGuide', 'Expedia', 'Direct booking'];

// Rating attributes config
export const RATING_ATTRS = [
  { key: 'snorkeling_quality' as const, label: 'Snorkeling' },
  { key: 'food_quality' as const, label: 'Food' },
  { key: 'open_bar' as const, label: 'Open Bar' },
  { key: 'crew' as const, label: 'Crew' },
];

export interface FilterState {
  company_name: string[];
  inclusions: string[];
  vibe: Vibe[];
  crowd_size: CrowdSize[];
  boarding: Boarding[];
  best_for: BestFor[];
  available_to: AvailableTo[];
  boat_type: BoatType[];
  categories: Category[];
  time_of_day: TimeOfDay[];
  departure_point: DeparturePoint[];
  pick_up: PickUp[];
  price_min: number | null;
  price_max: number | null;
  review_min: number | null;
  duration_min: number | null;
  duration_max: number | null;
  snorkeling_min: number | null;
  food_min: number | null;
  open_bar_min: number | null;
  crew_min: number | null;
  snorkeling_no: boolean;
  food_no: boolean;
  open_bar_no: boolean;
  crew_no: boolean;
}

export const emptyFilters: FilterState = {
  company_name: [],
  inclusions: [],
  vibe: [],
  crowd_size: [],
  boarding: [],
  best_for: [],
  available_to: [],
  boat_type: [],
  categories: [],
  time_of_day: [],
  departure_point: [],
  pick_up: [],
  price_min: null,
  price_max: null,
  review_min: null,
  duration_min: null,
  duration_max: null,
  snorkeling_min: null,
  food_min: null,
  open_bar_min: null,
  crew_min: null,
  snorkeling_no: false,
  food_no: false,
  open_bar_no: false,
  crew_no: false,
};
